// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as Redux from 'redux';
import styled from 'styled-components';
import { translate } from "react-i18next";

import UploadTypeSelect from './uploadTypeSelect/UploadTypeSelect';
import GroupAttributesForm from './groupAttributesForm/GroupAttributesForm';
import Dropzone from './dropzone/Dropzone';
import FilesList from './filesList/FilesList';
import UploadButton from './uploadButton/UploadButton';
import messageManager from '../../../../utilities/messageManager';
import BackLink from '../../../../components/BackLink';
import {
    PHOTOS_TYPE,
    VIDEO_TYPE,
    DRONE_TYPE,
    MODEL_TYPE
} from '../../../../types/upload';
import UploadDescription from './uploadDescription/UploadDescription';
import UploadProgressBar from './uploadProgressBar/UploadProgressBar';
import { uploadClearStatus } from '../../../../actions/upload';

type Props = {
    t: (s: string) => string,
    roles: string,
    uploadMessage: Object,
    uploadProgress: number,
};

type State = {
    activePage: number,
    groupAttributes: Object,
    files: Array<Object>,
    type: string,
};

const UploadPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - 20px);
  justify-content: flex-start;
  padding: 0 10px 10px 10px;
`;

const Container = styled.div`
    border: 1px solid #ebebeb;
    border-radius: 5px;
    background-color: #ffffff;
    width: calc(100% - 20px);
    padding: 5px 10px;
    margin-bottom: 10px;
`;

const UploadTitle = styled.p`
    text-align: center;
    margin: 5px;
    color: #265a9c;
    font-weight: bold;
    white-space: pre-wrap;
`;

class UploadPanel extends React.Component<Props, State> {
    constructor(props) {
        super();
        this.state = {
            activePage: 1,
            groupAttributes: { clear: false, valid: false, attributes: {} },
            files: [],
            type: null,
        }
    }

    onTypeSelect(type) {
        this.props.uploadClearStatus();
        this.setState({
            activePage: 2,
            type: type,
        });
        // if flight is chosen attributes are immediataly valid
        if (this.props.chosenFlight && (type === PHOTOS_TYPE || type === VIDEO_TYPE || type === DRONE_TYPE)) {
            this.setState({
                groupAttributes: {
                    clear: false,
                    valid: true,
                    attributes: {
                        projectId: this.props.chosenFlight.id,
                        timeZoneName: '(UTC) GMT Standard Time'
                    }
                }
            });
        }
    }

    onGroupAttributesChange(value) {
        this.setState({
            groupAttributes: value
        })
    }

    onFilesLoad(files) {
        this.props.uploadClearStatus();
        if (this.state.type === PHOTOS_TYPE) {
            this.setState({
                files: this.state.files.concat(files),
            });
        } else {
            this.setState({
                files: files
            })
        }
    }

    onFilesDelete(files) {
        this.setState({
            files: files
        })
    }

    componentDidUpdate(prevProps) {
        const { t, uploadMessage } = this.props;
        if (prevProps.uploadMessage !== uploadMessage && (uploadMessage.result === 'success' || uploadMessage.result === 'fail')) {
            uploadMessage.text = t(uploadMessage.text);
            messageManager.show(uploadMessage);
            this.setState({
                files: [],
            })
        }
    }

    render(): React.Node {
        const { t, roles, uploadProgress, uploadMessage } = this.props;
        const { files, type, activePage, groupAttributes } = this.state;

        if (activePage === 1) {
            if (roles === 'Regular') {
                return (
                    <UploadPanelWrapper style={{ alignItems: 'flex-start' }}>
                        <UploadTitle>
                            {t('You dont have necessary permissions')}
                        </UploadTitle>
                    </UploadPanelWrapper>
                )
            }
            else {
                return (
                    <UploadPanelWrapper style={{ alignItems: 'flex-start' }}>
                        <UploadTypeSelect
                            onTypeSelect={this.onTypeSelect.bind(this)}
                        />
                    </UploadPanelWrapper>
                )
            }
        }
        if (activePage === 2) {
            return (
                <UploadPanelWrapper style={{ alignItems: 'center' }}>
                    <BackLink
                        active={uploadProgress === 0}
                        style={{
                            marginBottom: '10px',
                            fontSize: '14px',
                        }}
                        onClick={() => {
                            if (uploadProgress === 0) {
                                this.setState({
                                    activePage: 1,
                                    files: [],
                                    groupAttributes: { clear: false, valid: false, attributes: {} },
                                    type: null
                                })
                            }
                        }}>
                        {`< ${t('Back to upload menu')}`}
                    </BackLink>
                    <Container>
                        <UploadTitle>
                            {`${t('Upload')}${type === 'shp' ? ' .' : ' '}${t(type)}`}
                        </UploadTitle>
                        <UploadDescription
                            type={type}
                        />
                    </Container>
                    <Container>
                        <GroupAttributesForm
                            t={t}
                            loadType={type}
                            onChangeAttributes={this.onGroupAttributesChange.bind(this)}
                            clear={this.state.groupAttributes.clear}
                            disabled={uploadProgress !== 0}
                        />
                    </Container>
                    {type !== MODEL_TYPE
                        ? <Dropzone
                            t={t}
                            onLoad={this.onFilesLoad.bind(this)}
                            loadType={type}
                            disabled={uploadProgress !== 0}
                        />
                        : null
                    }
                    <FilesList
                        t={t}
                        onDelete={this.onFilesDelete.bind(this)}
                        files={files}
                        disabled={uploadProgress !== 0}
                    />
                    <UploadProgressBar
                        visible={uploadMessage.result}
                        simpleUpload={type === PHOTOS_TYPE || type === DRONE_TYPE}
                        filesCounter={files.length}
                    />
                    <UploadButton
                        disabled={(type === MODEL_TYPE)
                            ? !groupAttributes.valid || uploadProgress !== 0
                            : uploadProgress !== 0 || files.length === 0 || !groupAttributes.valid}
                        loadType={type}
                        files={files}
                        groupAttributes={this.state.groupAttributes.attributes}
                        onChangeAttributes={this.onGroupAttributesChange.bind(this)}
                    />
                </UploadPanelWrapper >
            )
        }
    }
}

const mapStateToProps = state => ({
    chosenFlight: state.flightManager.get('chosenFlight'),
    roles: state.auth.get('roles'),
    uploadProgress: state.upload.get('uploadProgress'),
    uploadMessage: state.upload.get('uploadMessage'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
    uploadClearStatus
}, dispatch);

export default translate("translations")(connect(mapStateToProps, mapDispatchToProps)(UploadPanel));


// WEBPACK FOOTER //
// ./src/containers/content/grid/uploadPanel/UploadPanel.jsx